// src/components/privacy/PrivacyPolicy.jsx

// src/components/privacy/PrivacyPolicy.jsx

import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import Header from '../header/Header';
import PrivacyPolicyStyles from './PrivacyPolicy.styles';

const PrivacyPolicy = () => {
  return (
    <>
      <Header />
      <Box sx={PrivacyPolicyStyles.container}>
        <Typography variant="h4" sx={PrivacyPolicyStyles.title}>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" sx={PrivacyPolicyStyles.subtitle}>
          Effective Date: August 19, 2024
        </Typography>

        {/* Introduction */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Introduction</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            The Tenant Guided Tours products and services are owned and operated by Doors, Inc. and its affiliates, as applicable (“Tenant Guided Tours,” “us”, “our”, or “we”).
            We know you’re trusting us with your data. We also know we have a responsibility to respect your privacy, and we work hard to do just that. This notice explains what information we collect, why we use it, who we share it with, and how we protect it, along with the tools you can use to manage your privacy.
            <br /><br />
            We carefully evaluate how we use data to make sure that we’re using your information to provide value for you. On this page, we aim to explain everything you need to know about your privacy while using our services.
            If you have any questions that we haven’t answered here, you can always contact us at <Link href="mailto:info@mytenantaccess.com">info@mytenantaccess.com</Link>.
          </Typography>
        </Box>

        {/* Information Collected by Us */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Information Collected by Us</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            When you use our services, we collect a variety of information from and about you and your devices. Some of this information identifies you directly (like an email address), while some of it is associated with you through your account, profile, or device (like your location data, photo ID, proof of ownership, or management agreements).
            <br /><br />
            The kind of data we collect about you depends on how you use our services. Even if you don’t have an account with us, we may still collect some data about you, like your email address and phone number if you contact a real estate agent through our platform, or your IP address and other device information when you browse our sites or use our apps.
            <br /><br />
            <strong>Information you give us or create using our services:</strong> This usually includes your name, email, phone, and info used to verify your identity prior to providing rental property access codes.
            <br /><br />
            <strong>Information from public sources and third parties:</strong> We collect info from public sources (like county/state tax and property records, government agencies, MLS) and third parties (like Zillow, Craigslist, Facebook Marketplace, etc.) to provide services and verify properties/owners.
            <br /><br />
            <strong>Information we collect as you use our services:</strong>
            <ul>
              <li><strong>Activity information:</strong> Your search/viewing history, purchase activity, clicks, and how you use our website/app features. When you call/text us or a professional, we collect basic call/text metadata and content.</li>
              <li><strong>Device information:</strong> Data about your browsers/devices (model, settings, OS, identifiers, IP, crash reports, etc.) and your interactions with our services.</li>
              <li><strong>Location information:</strong> If enabled, we collect your device/browser location to personalize services. You can turn this off in your settings.</li>
              <li><strong>Cookies & tracking technologies:</strong> We and our partners use cookies, pixels, and similar tools to analyze how you use our site, personalize your experience, and show relevant ads. You may manage or disable cookies in your browser settings, but some features may not work if you do.</li>
              <li><strong>Social networks:</strong> If you use social/third-party logins (Google, Apple, etc.), we may access your public profile data. You can limit sharing using your social media settings.</li>
              <li><strong>Inferences:</strong> We use data about your activity to improve our services and infer your preferences.</li>
              <li><strong>Commercial information:</strong> We collect details about products/services you buy or consider buying from us.</li>
            </ul>
          </Typography>
        </Box>

        {/* How We Use Your Information */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>How We Use Your Information</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            We use your information to:
            <ul>
              <li>Verify your identity and process your transactions.</li>
              <li>Provide and improve our services, fix bugs, and personalize your experience (like customized search results and connecting you to leasing agents/properties).</li>
              <li>Communicate with you about your transactions, service updates, offers, promotions, and responses to your questions or requests.</li>
              <li>Detect, investigate, and prevent fraud, illegal activities, and protect our rights, property, and users.</li>
              <li>Comply with legal obligations.</li>
            </ul>
            If we ever want to use your information in a way not described here, we will notify you first so you can decide if you’re comfortable with it.
          </Typography>
        </Box>

        {/* Sharing Your Information */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Sharing Your Information</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            We share information only when needed for a legitimate business or legal purpose and only with recipients who protect your information.
            <ul>
              <li><strong>With your direction:</strong> When you ask us to share your data (e.g. contacting leasing staff), we share it as requested.</li>
              <li><strong>With service providers and business partners:</strong> We share with vendors/partners only as needed to provide services, under strict agreements and controls. In the event of a data breach, we will inform you and act swiftly to remedy any issues.</li>
              <li><strong>Legal and compliance transfers:</strong> If required by law, regulation, subpoena, or to protect rights and safety, we will share information only as needed and will inform you if allowed.</li>
              <li><strong>Business transfers:</strong> In case of merger, acquisition, or sale, your info may be transferred as part of that process.</li>
              <li><strong>Public & deidentified data:</strong> We may share aggregated or deidentified data that cannot reasonably identify you.</li>
              <li><strong>Sales of personal information:</strong> We allow certain partners to use cookies and pixels on our site, which may be considered a “sale” under state law. Other than this, we do not sell your personal information.</li>
            </ul>
            You can always disable cookies or tracking via our preference tools or your browser/device.
          </Typography>
        </Box>

        {/* Privacy Tools and Choices */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Your Privacy Tools and Choices</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            <strong>Accessing and deleting your information:</strong> If you are a California resident, state law may give you specific rights regarding your personal information (see “Legal Disclosures” below). If you delete your data or close your account, we will not be able to offer services that require your data.
            <br /><br />
            <strong>Managing cookie preferences:</strong> You can manage cookies in your browser or using our site tools. More info: <Link href="https://www.networkadvertising.org/" target="_blank">Network Advertising Initiative</Link>, <Link href="https://optout.aboutads.info/" target="_blank">Digital Advertising Alliance</Link>.
            <br /><br />
            <strong>Email subscriptions and newsletters:</strong> You can opt out or manage your preferences using links in our emails. Transactional or essential communications may still be sent if required.
            <br /><br />
            <strong>Calls and texts:</strong> By providing your phone number, you agree to be contacted regarding orders or services. You can always opt out of marketing texts/calls.
          </Typography>
        </Box>

        {/* Protecting Your Information */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Protecting Your Information</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            Tenant Guided Tours and Doors, Inc. build security into our products from the ground up. We use industry best practices to protect your information from unauthorized access, disclosure, alteration, and destruction. 
            <br /><br />
            No method of internet transmission or data storage is 100% secure, but we strive to notify you promptly if anything happens to your information.
            <br /><br />
            We are based in the United States and governed primarily by U.S. law. Where applicable, we comply with privacy laws of other countries.
            If you use our services or provide information to us, your information will be processed and stored in the U.S., where laws may differ from your local protections.
          </Typography>
        </Box>

        {/* Applicability */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Applicability of This Policy</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            This policy applies to Tenant Guided Tours services available for purchase through our website. Some features/services may link to or involve third-party sites. We do not control those sites, and this privacy policy does not apply to them.
          </Typography>
        </Box>

        {/* Legal Disclosures */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Legal Disclosures (including CCPA)</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            Under the California Consumer Privacy Act (CCPA), California residents have the right to:
            <ul>
              <li>Know the categories of personal information we’ve collected, and the categories of sources for that info.</li>
              <li>Know the business purposes for collecting/sharing personal information.</li>
              <li>Know the categories of third parties with whom we share information.</li>
              <li>Access and delete your specific personal information.</li>
              <li>Correct inaccurate personal information.</li>
              <li>Opt out of the sale of your information and limit use of sensitive information.</li>
              <li>Not be discriminated against for exercising your privacy rights.</li>
            </ul>
            If you have questions, email us at <Link href="mailto:info@mytenantaccess.com">info@mytenantaccess.com</Link>.
          </Typography>
        </Box>

        {/* Changes to This Policy */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Changes to This Policy</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            We review our privacy practices regularly and may update this policy to reflect changes in our products, technology, or use of data. We won’t reduce your rights or significantly change the way we use/share data without telling you first and giving you a chance to stop using our services if you choose.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={PrivacyPolicyStyles.section}>
          <Typography variant="h6" sx={PrivacyPolicyStyles.sectionTitle}>Contacting Us</Typography>
          <Typography variant="body1" sx={PrivacyPolicyStyles.text}>
            If you have any questions about this privacy notice or our privacy practices, please contact us:
            <br /><br />
            <strong>Tenant Guided Tours</strong><br />
            Attn: Consumer Care<br />
            600 17th Street Suite 2800 South #2804V<br />
            Denver, CO 80202<br />
            <strong>Email:</strong> <Link href="mailto:info@mytenantaccess.com">info@mytenantaccess.com</Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default PrivacyPolicy;
